// src/services/rateLimitService.js
/**
 * Service to handle API rate limiting
 */
const rateLimitService = {
  // Store rate limit information
  limits: {
    remaining: null,
    limit: null,
    resetAt: null,
    retryAfter: null
  },
  
  // Cache of endpoints currently being retried
  retryCache: new Map(),
  
  /**
   * Extract rate limit information from response headers
   * @param {Object} headers - Response headers
   */
  updateLimits: (headers) => {
    // Parse rate limit headers (adjust names based on your API)
    rateLimitService.limits = {
      remaining: parseInt(headers['x-ratelimit-remaining'] || '-1', 10),
      limit: parseInt(headers['x-ratelimit-limit'] || '-1', 10),
      resetAt: headers['x-ratelimit-reset'] 
        ? new Date(parseInt(headers['x-ratelimit-reset'] * 1000, 10))
        : null,
      retryAfter: headers['retry-after'] 
        ? parseInt(headers['retry-after'], 10) 
        : null
    };
    
    // Dispatch event for potential UI updates
    window.dispatchEvent(new CustomEvent('ratelimit-updated', { 
      detail: rateLimitService.limits 
    }));
  },
  
  /**
   * Get current rate limit information
   * @returns {Object} Rate limit info
   */
  getLimits: () => {
    return { ...rateLimitService.limits };
  },
  
  /**
   * Check if we're approaching rate limit
   * @returns {boolean} True if approaching limit
   */
  isApproachingLimit: () => {
    const { remaining, limit } = rateLimitService.limits;
    if (remaining === null || limit === null) return false;
    
    // Consider approaching limit if less than 10% remaining
    return remaining > 0 && remaining / limit < 0.1;
  },
  
  /**
   * Calculate backoff time for retrying
   * @param {number} attempt - Which retry attempt
   * @returns {number} Time to wait in ms
   */
  calculateBackoff: (attempt) => {
    // Use retry-after if available
    if (rateLimitService.limits.retryAfter) {
      return rateLimitService.limits.retryAfter * 1000;
    }
    
    // Otherwise use exponential backoff with jitter
    const baseDelay = 1000; // 1 second
    const maxDelay = 60000; // 1 minute
    const expBackoff = Math.min(
      maxDelay,
      baseDelay * Math.pow(2, attempt)
    );
    
    // Add jitter (±25%)
    const jitter = 0.5 - Math.random();
    return expBackoff + (expBackoff * jitter * 0.5);
  },
  
  /**
   * Handle a rate limited request
   * @param {Object} config - Axios request config
   * @param {number} attempt - Retry attempt number
   * @returns {Promise} - Promise to retry the request
   */
  handleRateLimit: (config, attempt = 0) => {
    const requestKey = `${config.method}:${config.url}`;
    
    // Avoid duplicate retries for the same request
    if (rateLimitService.retryCache.has(requestKey)) {
      return rateLimitService.retryCache.get(requestKey);
    }
    
    const backoffTime = rateLimitService.calculateBackoff(attempt);
    
    // Create retry promise
    const retryPromise = new Promise((resolve) => {
      setTimeout(() => {
        // Remove from cache once timeout completes
        rateLimitService.retryCache.delete(requestKey);
        resolve();
      }, backoffTime);
    });
    
    // Cache the promise
    rateLimitService.retryCache.set(requestKey, retryPromise);
    
    // Return the retry promise
    return retryPromise;
  }
};

export default rateLimitService;